import React, { useState, useEffect } from 'react';
import { MapPin, Plus, Edit, Trash2, ChevronDown, ChevronRight, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Location } from '@/types';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';
import LocationForm from './LocationForm';

interface LocationListProps {}

const LocationList: React.FC<LocationListProps> = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await apiService.get<Location[]>('/locations');
      setLocations(response || []);
      
      // Standardmäßig alle Locations aufklappen, die Kinder haben
      const locationsWithChildren = (response || [])
        .filter(loc => (response || []).some(child => child.parentId === loc.id))
        .map(loc => loc.id);
      setExpandedLocations(new Set(locationsWithChildren));
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Fehler beim Laden der Standorte');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLocation = () => {
    setEditingLocation(null);
    setShowForm(true);
  };

  const handleEditLocation = (location: Location) => {
    setEditingLocation(location);
    setShowForm(true);
  };

  const handleDeleteLocation = async (location: Location) => {
    if (!confirm(`Möchten Sie den Standort "${location.name}" wirklich löschen?`)) {
      return;
    }

    try {
      await apiService.delete(`/locations/${location.id}`);
      toast.success('Standort erfolgreich gelöscht');
      fetchLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error('Fehler beim Löschen des Standorts');
    }
  };

  const handleFormSubmit = async () => {
    setShowForm(false);
    setEditingLocation(null);
    await fetchLocations();
  };

  const toggleExpanded = (locationId: string) => {
    const newExpanded = new Set(expandedLocations);
    if (newExpanded.has(locationId)) {
      newExpanded.delete(locationId);
    } else {
      newExpanded.add(locationId);
    }
    setExpandedLocations(newExpanded);
  };

  const getChildLocations = (parentId: string | null) => {
    return locations.filter(loc => loc.parentId === parentId);
  };

  const renderLocationTree = (parentId: string | null, level: number = 0) => {
    const childLocations = getChildLocations(parentId);
    
    return childLocations.map(location => {
      const hasChildren = locations.some(loc => loc.parentId === location.id);
      const isExpanded = expandedLocations.has(location.id);
      
      return (
        <div key={location.id} className="space-y-2">
          <div 
            className={`flex items-center justify-between p-3 rounded-lg border ${
              level === 0 ? 'bg-card' : 'bg-muted/50'
            }`}
            style={{ marginLeft: `${level * 20}px` }}
          >
            <div className="flex items-center space-x-3">
              {hasChildren && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleExpanded(location.id)}
                  className="h-6 w-6 p-0"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              )}
              {!hasChildren && <div className="w-6" />}
              
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <div>
                <h3 className="font-medium">
                  {location.name}
                  {location.plantNumber && (
                    <span className="text-muted-foreground ml-2">({location.plantNumber})</span>
                  )}
                </h3>
                {location.description && (
                  <p className="text-sm text-muted-foreground">{location.description}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Badge variant={level === 0 ? "default" : "secondary"}>
                {level === 0 ? 'Standort' : 'Unterstandort'}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEditLocation(location)}
                className="h-8 w-8 p-0"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteLocation(location)}
                className="h-8 w-8 p-0 text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {hasChildren && isExpanded && (
            <div className="ml-4">
              {renderLocationTree(location.id, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Standortverwaltung</h1>
          <p className="text-muted-foreground">
            Verwalten Sie Standorte und Unterstandorte hierarchisch
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => {
              const allLocationIds = locations
                .filter(loc => locations.some(child => child.parentId === loc.id))
                .map(loc => loc.id);
              
              if (expandedLocations.size === allLocationIds.length) {
                setExpandedLocations(new Set());
              } else {
                setExpandedLocations(new Set(allLocationIds));
              }
            }}
            className="flex items-center space-x-2"
          >
            {expandedLocations.size > 0 ? (
              <>
                <ChevronRight className="h-4 w-4" />
                <span>Alle einklappen</span>
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                <span>Alle aufklappen</span>
              </>
            )}
          </Button>
          <Button onClick={handleCreateLocation} className="flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Neuer Standort</span>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Standorte ({filteredLocations.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-4">
            <Input
              placeholder="Standorte durchsuchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
          </div>

          <div className="space-y-2">
            {filteredLocations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchQuery ? 'Keine Standorte gefunden' : 'Keine Standorte vorhanden'}
              </div>
            ) : (
              renderLocationTree(null)
            )}
          </div>
        </CardContent>
      </Card>

      {showForm && (
        <LocationForm
          location={editingLocation}
          onClose={() => {
            setShowForm(false);
            setEditingLocation(null);
          }}
          onSubmit={handleFormSubmit}
          locations={locations}
        />
      )}
    </div>
  );
};

export default LocationList;
